export const assetNodeTypes = [
  { id: 1, title: "Site" },
  { id: 2, title: "Department" },
  { id: 3, title: "Maintenance object" },
];

export const getAssetNodeTypeLabel = (nodeType) => {
  return assetNodeTypes.find((type) => type.id === Number(nodeType))?.title ?? nodeType;
};

export const canAssignMachine = (node) => {
  return node?.nodeType === 3 && node?.isActive;
};

// The range AssetService accepts. Next year is allowed because a machine is often sold as next
// year's model before the year turns. UTC, like the server's check, so the two agree on New
// Year's night.
export const MIN_MODEL_YEAR = 1900;
export const getMaxModelYear = (now = new Date()) => now.getUTCFullYear() + 1;

/**
 * The model year field's text as the API wants it: null when blank, otherwise the number.
 *
 * Digits only, rather than Number() on whatever was typed. Number() reads "0x7E3" as 2019 and
 * "" as 0, and the second would turn a cleared field into a year the server rejects. Anything
 * that is not plain digits comes back NaN, which isValidModelYear refuses.
 */
export const parseModelYear = (text) => {
  const trimmed = String(text ?? "").trim();
  if (trimmed === "") return null;
  return /^\d+$/.test(trimmed) ? Number(trimmed) : NaN;
};

// Null is valid: the year is optional, and blank is how the user clears it.
export const isValidModelYear = (year, now = new Date()) =>
  year === null || (Number.isInteger(year) && year >= MIN_MODEL_YEAR && year <= getMaxModelYear(now));

/**
 * Depth-first, so the order this returns is the tree's own reading order — the order the table
 * view relies on when no column is sorted.
 *
 * `ancestorNames` is root-first and excludes the node's own name, which makes the table's Path
 * column a join and leaves `parentName` as nothing more than its last element. A root gets an
 * empty array rather than a missing field, so callers can join without guarding.
 *
 * `ancestorIds` is the same path as ids: exactly the nodes that have to be expanded for this one
 * to be on screen in the tree.
 */
export const flattenAssetNodes = (nodes, level = 0, parentName = "", ancestorNames = [], ancestorIds = []) => {
  return (nodes ?? []).flatMap((node) => {
    const flatNode = { ...node, level, parentName, ancestorNames, ancestorIds };
    return [
      flatNode,
      ...flattenAssetNodes(node.children, level + 1, node.name, [...ancestorNames, node.name], [...ancestorIds, node.id]),
    ];
  });
};

/**
 * Hands back the same Set when every id is already in it, so a setState fed from this is a no-op
 * rather than a re-render of the whole tree on every selection.
 */
export const expandIds = (expandedIds, ids) => {
  if ((ids ?? []).every((id) => expandedIds.has(id))) return expandedIds;
  return new Set([...expandedIds, ...ids]);
};

export const toggleExpandedId = (expandedIds, id) => {
  const next = new Set(expandedIds);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
};

export const getAllowedChildTypeOptions = (parentNode) => {
  if (!parentNode) return [];

  switch (parentNode.nodeType) {
    case 1:
      return assetNodeTypes.filter((type) => type.id === 2);
    case 2:
      return assetNodeTypes.filter((type) => type.id === 2 || type.id === 3);
    case 3:
      return assetNodeTypes.filter((type) => type.id === 3);
    default:
      return [];
  }
};

/**
 * Types that may stand without a parent. Site is the only one, since nothing in
 * canParentNodeType accepts it as a child.
 *
 * Kept apart from getAllowedChildTypeOptions on purpose: there, `null` means "no node is
 * selected", and several call sites rely on that returning nothing. Answering "Site" to a
 * missing selection would drop a stray Site option into menus that must not offer one.
 */
export const getRootTypeOptions = () => {
  return assetNodeTypes.filter((type) => type.id === 1);
};

export const canParentNodeType = (parentType, childType) => {
  return (
    (parentType === 1 && childType === 2) ||
    (parentType === 2 && childType === 2) ||
    (parentType === 2 && childType === 3) ||
    (parentType === 3 && childType === 3)
  );
};

export const getDescendantIds = (node) => {
  return (node?.children ?? []).flatMap((child) => [child.id, ...getDescendantIds(child)]);
};

export const getValidReparentTargets = (nodes, node) => {
  if (!node || node.nodeType === 1 || !node.parentAssetNodeId) return [];

  const blockedIds = new Set([node.id, ...getDescendantIds(node)]);
  return flattenAssetNodes(nodes)
    .filter((target) => !blockedIds.has(target.id))
    .filter((target) => target.id !== node.parentAssetNodeId)
    .filter((target) => target.isActive)
    .filter((target) => canParentNodeType(target.nodeType, node.nodeType));
};

export const toSelectOptions = (nodes) => {
  return nodes.map((node) => ({
    id: node.id,
    title: `${"  ".repeat(node.level ?? 0)}${node.name}`,
  }));
};
